import React from 'react';

import { useBookingPaymentStatus } from '@/booking-payment';
import { useActiveBrandConfig } from '@/brand';
import { HotelStructuredData, getReservationStatusForBooking } from '@/data';
import ReservationStructuredData from '@/data/ReservationStructuredData.component';
import { CurrencyCode } from '@/finance/utils/formatting';
import { useHotel } from '@/hotel';
import { getCountryName } from '@/places';

import { useBooking } from '../hooks';
import { useBookingStatus } from '../hooks/use-booking-status.hook';

interface BookingStructuredDataContainerProps {
  bookingId: string;
  hotelSlug: string;
}

/**
 * Adds JSON-LD to the page's <head /> about the booking matching the given ID.
 */
export const BookingStructuredDataContainer = (
  props: BookingStructuredDataContainerProps
) => {
  const booking = useBooking({
    bookingId: props.bookingId,
    hotelSlug: props.hotelSlug,
  });
  const bookingStatus = useBookingStatus({
    bookingId: props.bookingId,
    hotelReferenceId: props.hotelSlug,
  });
  const { name: brandName } = useActiveBrandConfig();
  const hotel = useHotel(props.hotelSlug);
  const [paymentStatus] = useBookingPaymentStatus(props.bookingId);

  if (!booking || !hotel || !bookingStatus) {
    return null;
  }

  return (
    <>
      <HotelStructuredData
        name={hotel.name}
        identifier={hotel.code}
        brand={brandName}
        telephone={hotel.contact.phone}
        image={hotel.content.images.hero?.source}
        address={{
          streetAddress: hotel.location.address.lines.join(', '),
          addressRegion: hotel.location.address.city,
          postalCode: hotel.location.address.postcode,
          addressCountry: getCountryName(hotel.location.address.countryCode),
        }}
      />
      <ReservationStructuredData
        reservationId={booking.id}
        checkinTime={booking.firstRoomStay.from}
        checkoutTime={booking.firstRoomStay.to}
        totalPrice={
          booking.firstTotalChargeBreakdown.grandTotal?.fixedValue ?? ''
        }
        priceCurrency={
          hotel.payments.currencyCode.toUpperCase() as CurrencyCode
        }
        reservationStatus={getReservationStatusForBooking(
          bookingStatus.status,
          paymentStatus === 'submitting'
        )}
      />
    </>
  );
};
